import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/Roles.module.scss';
import { Checkbox } from "antd";
import { Icon, Spin, Tooltip } from "Components";
import { ROLE_NAME_MAX_LENGTH, isRoleNameOverMaxLength } from '../Roles.helpers';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'Components/Permission/Permission.hooks';
import { PermissionCode } from 'Services/Api/Users/Types';
import {  getPermissionTranslationKey, PermissionGroupName } from 'Services/Api/Roles/Types';
import { getPermissionGroupDisplayName } from './PermissionDisplayMapper';
import { useStores } from 'Hooks/useStore';
import { getAppRoutes } from 'Pages/App/App.constants';
import { useHistory } from 'react-router-dom';
import { buildPermissionList, FORCED_DEPENDENCIES, getPermissionGroupTranslationKey, getPermissionSectionTranslationKey, getViewPermissionCode, PERMISSION_TEMPLATE, PermissionGroup as PermissionSpecificGroup } from '../utils/permissions.utils';
import { usePermissionList } from '../Roles.hooks';

export type PermissionMode = 'view' | 'add' | 'edit' | 'clone';

export interface TextFieldProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  disabled?: boolean;
  maxLength?: number;
  error?: string;
  tooltip?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface CustomCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}

export interface PermissionSectionProps {
  section: PermissionSectionData;
  checkedMap: Record<string, boolean>;
  permissionList: PermissionSectionData[];
  onToggleItem: (key: string, checked: boolean, items:PermissionItem[]) => void;
  onToggleAll: (section: PermissionSectionData, checked: boolean) => void;
  isLast: boolean;
  readOnly?: boolean;
}

export interface SubGroupTitleProps {
  children: React.ReactElement | string;
}

export interface CheckboxRowProps {
  items: PermissionItem[];
  permissionList: PermissionSectionData[];
  checkedMap: Record<string, boolean>;
  onToggle: (key: string, checked: boolean, items: PermissionItem[]) => void;
  readOnly?: boolean;
}

export interface PermissionItem {
  key: string;
  label: string;
  code: string;
  checked?: boolean;
  disabled?: boolean;
}

export interface PermissionGroup {
  title?: string;
  items: PermissionItem[];
}

export interface PermissionSectionData {
  key: string;
  label: string;
  code?: string;
  groups: PermissionGroup[];
}

export interface RoleFormValues {
  roleId: number;
  roleName?: string;
  isPrimary?: boolean;
  description?: string;
  permissions: any[]
  /** Only relevant for clone mode - the role being cloned from */
  sourceRoleName?: string;
}

export interface RoleSubmitPayload {
  roleName: string;
  description: string;
  permissions: number[];
}

export interface PermissionListProps {
  mode: PermissionMode;
  permissionList: PermissionSectionData[];
  initialValues?: RoleFormValues;
  loading?: boolean;
  onCancel?: () => void;
  setMode?: React.Dispatch<React.SetStateAction<'edit' | 'view'>>;
  onSubmit?: (values: RoleSubmitPayload) => void;
}

function TextField({ label, required, placeholder, value, onChange, disabled, maxLength, error, tooltip }: TextFieldProps) {
  const input = (
    <div
      className={`cr-input${error ? ' cr-input--error' : ''}`}
      style={{ backgroundColor: disabled ? '#EAEAEB' : 'inherit' }}
    >
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className="cr-input-value"
        aria-invalid={!!error}
        aria-describedby={error ? `${label}-error` : undefined}
      />
    </div>
  );

  return (
    <div className="cr-input-wrap">
      <div className="cr-label-row">
        <span className="cr-label-text">{label}</span>
        <span className="cr-asterisk" style={{ visibility: required ? 'visible' : 'hidden' }}>*</span>
      </div>
      {tooltip ? (
        <Tooltip
          title={tooltip}
          enabled={!!tooltip}
          placement="topLeft"
          overlayClassName="role-cell-tooltip"
        >
          {input}
        </Tooltip>
      ) : (
        input
      )}
      {error && (
        <span id={`${label}-error`} className="cr-field-error">
          {error}
        </span>
      )}
    </div>
  );
}

function CustomCheckbox({ checked, disabled, onChange, label }: CustomCheckboxProps) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onChange={() => !disabled && onChange(!checked)}
    >
      {label}
    </Checkbox>
  );
}

function SubGroupTitle({ children }: SubGroupTitleProps) {
  return (
    <div className="cr-subgroup-title-wrap">
      <span className="cr-subgroup-title">{children}</span>
    </div>
  );
}

const isViewLocked = (viewCode: string, items: PermissionItem[], checkedMap: Record<string, boolean>, userPermissionCodes?: PermissionItem[]) => {
  if (viewCode === PermissionCode.UserManagerScreenView) {
    return !!userPermissionCodes?.some(item => checkedMap[item.key]);
  }

  return items.some(item => {
    if (item.code.startsWith('view_') || item.code.endsWith('view_page')) return false;

    return (
      getViewPermissionCode(item.code) === viewCode &&
      checkedMap[item.key]
    );
  });
};

function CheckboxRow({ items, checkedMap, onToggle, readOnly, permissionList }: CheckboxRowProps) {
  const { t } = useTranslation(); 
  const userPermissionCodes = permissionList.find(el => el.key === 'Settings')?.groups?.find(el => el.title === 'Users')?.items;   
  return (
    <div className="cr-checkbox-row">
      {items.map((item: PermissionItem) => (
        <div key={item.key} className="cr-checkbox-cell">
          <CustomCheckbox
            label={t(getPermissionTranslationKey(item.code))}
            checked={checkedMap[item.key] ?? false}
            disabled={readOnly || item.disabled || isViewLocked(item.code, items, checkedMap, userPermissionCodes)}
            onChange={(val: boolean) => onToggle(item.key, val, items)}
          />
        </div>
      ))}
    </div>
  );
}

function PermissionSection({ section, checkedMap, permissionList, onToggleItem, onToggleAll, isLast, readOnly }: PermissionSectionProps) {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const allItems: PermissionItem[] = section.groups.flatMap((g: PermissionGroup) => g.items);
  const selectableItems = allItems.filter((i) => !i.disabled);
  const allChecked = selectableItems.length > 0 && selectableItems.every((i) => checkedMap[i.key]);
  const someChecked = allItems.some((i) => checkedMap[i.key]);

  return (
    <div className={`cr-section ${!isLast ? 'cr-section-border' : ''}`}>
      <div className="cr-section-inner">
        <div className="cr-section-body">
          <div className="cr-section-header">
            <div className="cr-section-header-left"
              onClick={() => !readOnly && onToggleAll(section, !allChecked)}>
              <span
                className={`cr-checkbox-box ${allChecked ? 'cr-checkbox-box-checked' : someChecked ? 'cr-checkbox-box-partial' : ''
                  } ${readOnly ? 'cr-checkbox-disabled' : ''}`}
              >
                 <Checkbox checked={(allChecked)} />
              </span>
              <span className="cr-section-label">{t(getPermissionSectionTranslationKey(section.label))}</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="cr-collapse-btn"
              aria-label={open ? t('roles.permissionList.collapse') : t('roles.permissionList.expand')}
            >
              {open ? <Icon type="chevronUp" /> : <Icon type="chevronDown" />}
            </button>
          </div>

          {open && (
            <div className="cr-section-groups">
              {section.groups.map((group: PermissionGroup, gi: number) => (
                <div key={gi} className="cr-group">
                  {group.title && <SubGroupTitle>{t(getPermissionGroupTranslationKey(group.title))}</SubGroupTitle>}
                  <CheckboxRow
                    permissionList={permissionList}
                    items={group.items}
                    checkedMap={checkedMap}
                    onToggle={onToggleItem}
                    readOnly={readOnly}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function buildInitialCheckedMap(permissionList: PermissionSectionData[],
  selectedPermissionIds: string[] = [],) {
  const selected = new Set(selectedPermissionIds.map(String));

  const map: Record<string, boolean> = {};

  permissionList.forEach(section =>
    section.groups.forEach(group =>
      group.items.forEach(item => {
        map[item.key] = selected?.has(String(item.key));
      }),
    ),
  );
  return map;
}

// Main component

function PermissionList({
  mode,
  setMode,
  permissionList: updatedPermissionList,
  initialValues,
  loading,
  onCancel,
  onSubmit
}: PermissionListProps) {
  const { t } = useTranslation();

  const MODE_CONFIG: Record<
    PermissionMode,
    {
      heading: string;
      submitLabel: string;
      fieldsEditable: boolean;
      permissionsEditable: boolean;
      showFooter: boolean;
    }
  > = {
    view: {
      heading: t('roles.permissionList.heading.viewRoleDetail'),
      submitLabel: t('roles.permissionList.buttons.close'),
      fieldsEditable: false,
      permissionsEditable: false,
      showFooter: true
    },
    add: {
      heading: t('roles.permissionList.heading.add'),
      submitLabel: t('roles.permissionList.buttons.addRole'),
      fieldsEditable: true,
      permissionsEditable: true,
      showFooter: true
    },
    edit: {
      heading: t('roles.permissionList.heading.edit'),
      submitLabel: t('roles.permissionList.buttons.updateRole'),
      fieldsEditable: true,
      permissionsEditable: true,
      showFooter: true
    },
    clone: {
      heading: t('roles.permissionList.heading.clone'),
      submitLabel: t('roles.permissionList.buttons.cloneRole'),
      fieldsEditable: true,
      permissionsEditable: true,
      showFooter: true
    } 
  };

  const config = MODE_CONFIG[mode];
  const readOnly = !config.permissionsEditable;
  const fieldsDisabled = !config.fieldsEditable || loading || initialValues?.isPrimary || mode === 'view';
  
  const { systemsStore, authStore } = useStores();
  const { selectedTenant } = systemsStore;
  const history = useHistory();
  const [roleName, setRoleName] = useState(initialValues?.roleName ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const { getHasPermission } = usePermission();
  const HIDE_FOR_NON_ADMIN = useMemo(() => {
    if (mode === 'view' || mode === 'edit') {
        const sourceRole = (
            initialValues?.sourceRoleName ?? 
            initialValues?.roleName ?? 
            ''
        ).toLowerCase().trim();
        return sourceRole !== 'admin';
    }
    return true;
}, [mode, initialValues?.sourceRoleName, initialValues?.roleName]);
const permissionList = useMemo(
  () => buildPermissionList(updatedPermissionList, HIDE_FOR_NON_ADMIN),
  [updatedPermissionList, HIDE_FOR_NON_ADMIN],
);
  const [checkedMap, setCheckedMap] =
    useState<Record<string, boolean>>(() =>
      buildInitialCheckedMap(
        permissionList,
        initialValues?.permissions,
      ),
    );
  const checkedMapInitialized = useRef(false);


   const initialCheckedMap = useMemo(
    () => buildInitialCheckedMap(permissionList, initialValues?.permissions),
    [permissionList, initialValues?.permissions],
  );
     const permissionsChanged = useMemo(() => {
    const keys = new Set([...Object.keys(checkedMap), ...Object.keys(initialCheckedMap)]);
    for (const key of keys) {
      if (!!checkedMap[key] !== !!initialCheckedMap[key]) return true;
    }
    return false;
  }, [checkedMap, initialCheckedMap]);

  const hasChanges =
    roleName !== (initialValues?.roleName ?? '') ||
    description !== (initialValues?.description ?? '') ||
    permissionsChanged;

    const selectedPermissionsCount = Object.values(checkedMap)
  .filter(Boolean)
  .length;

const roleNameOverLimit = isRoleNameOverMaxLength(roleName);
const roleNameError = roleNameOverLimit
  ? t('roles.permissionList.fields.roleNameMaxLength', { max: ROLE_NAME_MAX_LENGTH })
  : undefined;

const isFormInvalid =
  !roleName.trim() ||
  roleNameOverLimit ||
  selectedPermissionsCount === 0;

  const canEdit = initialValues?.isPrimary ? !!getHasPermission({ code: PermissionCode.EditSystemRole }).permitted: !!getHasPermission({ code: PermissionCode.EDIT_SECONDARY_ROLE }).permitted;
  const toggleItem = (
    key: string,
    val: boolean,
    items: PermissionItem[]
  ) => {
    if (readOnly) return;

    const item = items.find(item => item.key === key);
    const isViewPermission = (code: string) =>  code.startsWith('view_') || code.endsWith('_view_page');
    if (!item) return;

    setCheckedMap(prev => {
      const next = {
        ...prev,
        [key]: val,
      };

    if (val && !isViewPermission(item.code)) {
      const viewPermissionCode = getViewPermissionCode(item.code);

      const viewPermission = items.find(
        permission => permission.code === viewPermissionCode,
      );

      if (viewPermission) {
        next[viewPermission.key] = true;
      }
    }
      const viewPermissionCode = getViewPermissionCode(item.code) ?? '';
      if (val && FORCED_DEPENDENCIES[viewPermissionCode]) {
        FORCED_DEPENDENCIES[viewPermissionCode].forEach(depCode => {
          const depItem = permissionList.flatMap(section => section.groups.flatMap(group => group.items)).find(permission => permission.code === depCode);
          if (depItem) next[depItem.key] = true;
        });
      }

      return next;
    });
  };

  const toggleAllInSection = (section: PermissionSectionData, val: boolean) => {
    if (readOnly) return;
    setCheckedMap((prev) => {
      const next = { ...prev };
      section.groups.forEach((g) =>
        g.items.forEach((i) => {
          if (!i.disabled) next[i.key] = val;
        })
      );
      return next;
    });
  };

  const handleSubmit = () => {
    if (mode === 'view') {
      onCancel?.();
      return;
    }
    if (isFormInvalid) {
      return;
    }
    const permissions = Object.keys(checkedMap).filter((key) => checkedMap[key]).map(Number);
    onSubmit?.({   roleName: roleName.trim(),
      description: description.trim(), permissions });
  };

  const subheading = useMemo(() => {
    if (initialValues?.sourceRoleName || mode === 'add') {
      let message = '';
      if(mode === 'clone') {
        message = t('roles.permissionList.cloneMessage');
      } else if(mode === 'view') {
        message = t('roles.permissionList.viewMessage');
      } else if(mode === 'edit') {
        message = t('roles.permissionList.editMessage');
      } else if(mode === 'add') {
        if(!authStore.isCurrentUserSuperAdmin && selectedTenant.value === -1) {
          history.push(getAppRoutes().ROLES);
          return;
        }
        message = t('roles.permissionList.createCustomRole');
      }
      return (
        <p className="cr-subheading">
          {message} <span className="cr-subheading-strong">{mode === 'add' ? selectedTenant.label : initialValues?.sourceRoleName}</span>.
        </p>
      );
    }
    return null;
  }, [mode, initialValues?.sourceRoleName, t]);

  useEffect(() => {
    if (!permissionList.length || checkedMapInitialized.current) {
      return;
    }

    const nextCheckedMap = buildInitialCheckedMap(
      permissionList,
      initialValues?.permissions,
    );

    const allItems = permissionList.flatMap(section => section.groups.flatMap(group => group.items));

    Object.keys(nextCheckedMap).forEach(key => {
      const permission = allItems.find(item => item.key === key);

      if (permission && nextCheckedMap[key] && (!permission.code.startsWith('view_')) && !permission.code.endsWith('view_page')) {
        const viewCode = getViewPermissionCode(permission.code);

        allItems
          .filter(item => item.code === viewCode)
          .forEach(item => {
            nextCheckedMap[item.key] = true;
          });
      }

      if (permission && nextCheckedMap[key] && FORCED_DEPENDENCIES[permission.code]) {
        FORCED_DEPENDENCIES[permission.code].forEach(depCode => {
          const depItem = allItems.find(i => i.code === depCode);
          if (depItem) nextCheckedMap[depItem.key] = true;
        });
      }
    });

    setCheckedMap(nextCheckedMap);
    checkedMapInitialized.current = true;
  }, [permissionList, initialValues?.permissions]);
  
  return (
    <>
    <div className="cr-page">
    {loading && <Spin overlay />}
      <div className="cr-heading-row">
        <Icon type="chevronLeft" onClick={onCancel} />
        <h1 className="cr-heading">{config.heading}</h1>
      </div>
      {subheading}
      <div className="cr-outer-frame">
        <div className="cr-card">
          <div className="cr-fields-row">
            <TextField
              label={t('roles.permissionList.fields.roleName')}
              required
              placeholder={t('roles.permissionList.fields.insertName')}
              value={roleName}
              disabled={fieldsDisabled}
              error={fieldsDisabled ? undefined : roleNameError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoleName(e.target.value)}
            />
            <TextField
              label={t('roles.permissionList.fields.description')}
              placeholder={t('roles.permissionList.fields.insertDescription')}
              value={description}
              disabled={!config.fieldsEditable || loading}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            />
          </div>

          <div className="cr-permission-block">
            <div className="cr-permission-title-row">
              <span className="cr-permission-title">{t('roles.permissionList.permission')}</span>
            </div>

            <div className="cr-permission-panel">
              {permissionList.map((section, i) => (
                <PermissionSection
                  key={section.key}
                  section={section}
                  checkedMap={checkedMap}
                  permissionList={permissionList}
                  onToggleItem={toggleItem}
                  onToggleAll={toggleAllInSection}
                  isLast={i === permissionList.length - 1}
                  readOnly={readOnly || Boolean(loading)}
                />
              ))}
            </div>
          </div>
        </div>

        {config.showFooter && (
          <div className="cr-footer-actions">
                <button
                  type="button"
                  className="cr-btn-ghost"
                  onClick={onCancel}
                >
                  {t('roles.permissionList.buttons.cancel')}
                </button>

              {mode === 'view' ? (
                canEdit ? 
                <button
                  type="button"
                  className="cr-btn-primary"
                  onClick={() => setMode?.('edit')}
                >
                  {t('roles.actions.edit')}
                </button> : <></>
              ) : (
                <button
                  type="button"
                  className="cr-btn-primary"
                  onClick={handleSubmit}
                  disabled={
                    loading ||
                    isFormInvalid ||
                    (mode === 'edit' && !hasChanges)
                  }
                >
                  {config.submitLabel}
                </button>
              )}
            </div>
        )}
      </div>
    </div>
    </>
  );
}

export default PermissionList;